import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { heartCountText } from "../../utils/heart_timer.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UICanvasRect } from "../common_ui_elements/ui_canvas_rect.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { addMirroredDialogBg } from "../common_ui_elements/mirrored_dialog_bg.js";
import { DialogCoinsBadge } from "../common_ui_elements/dialog_coins_badge.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { reportRvOfferShown } from "../../api/requests/economy.requests.js";
import { purchaseItem, rewardHeart } from "../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../api/player_sync.js";
import { heartTimerText } from "../../utils/heart_timer.js";
import { showRewarded } from "../../ads/rewarded.js";
import { areAdsBlockedOffline } from "../../ads/offline_ads.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../tooltip/offline_ad_tooltip.js";
import { AdsConfigService } from "../../ads/ads_config.js";
import {
  isRewardedCooldownActive,
  rewardedCooldownRemainingMs,
  rewardedCooldownTimerText,
  startRewardedCooldown,
} from "../../ads/rewarded_booster_cooldown.js";
import {
  logRewardedCompleted,
  logVirtualCurrencyEarned,
  logVirtualCurrencySpent
} from "../../analytics/appsflyer_events.js";
import { ApiError } from "../../api/http_client.js";
import { TransformRotate } from "../../transforms/transform_rotate.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { ITransformParams } from "../../types/transform_params.js";
import { Vector2 } from "../../common/vector2.js";

class SteppedRotate extends TransformRotate<UIImage>
{
  constructor(
    private readonly steps: number,
    target: UIImage,
    is_important: boolean,
    from: number | null,
    dest: number,
    anim_start_time: number,
    duration: number,
    optional_params: ITransformParams = {}
  )
  {
    super( target, is_important, from, dest, anim_start_time, duration, optional_params );
  }

  protected onUpdateTransform( eased: number ): void
  {
    super.onUpdateTransform( Math.floor( eased * this.steps ) / this.steps );
  }

  public override domEasing(): string
  {
    return `steps(${this.steps}, jump-end)`;
  }
}

export class MoreLivesDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 958;
  private static readonly DESIGN_H = 1500;
  private static readonly BG_W = 909;
  private static readonly BG_Y = 278;
  private static readonly BG_H = 1222;
  private static readonly INFO_FRAME_W = 678;
  private static readonly INFO_FRAME_H = 406;
  private static readonly INFO_FRAME_Y = 544;
  private static readonly BTN_W = 670;
  private static readonly BTN_H = 250;
  private static readonly FIRST_BTN_Y = 950;
  private static readonly BTN_STEP = 230;
  private static readonly BTN_OUTLINE_W = 8;
  private static readonly BTN_OUTLINE_OFFSET_Y = 2;

  private purchase_inflight = false;
  private buy_conflict_locked = false;
  private time_text: UITextElement | null = null;
  private heart_icon: UIImage | null = null;
  private heart_count_text: UITextElement | null = null;
  private buy_btn: UINineSliceElement | null = null;
  private purple_btn: UINineSliceElement | null = null;
  private buy_btn_texts: UITextElement[] = [];
  private purple_btn_texts: UITextElement[] = [];
  private buy_btn_active = true;
  private purple_btn_active = true;
  private reward_active_content: UIElement | null = null;
  private reward_cooldown_content: UIElement | null = null;
  private reward_cooldown_text: UITextElement | null = null;
  private reward_cooldown_visible = false;
  private shown_reward_cooldown_timer = "";
  private reward_debug_render_reason: string | null = null;
  private close_btn!: UIImage;
  private coins_badge: DialogCoinsBadge | null = null;
  private price_text: UITextElement | null = null;
  private reward_inflight = false;
  private shown_timer = "";
  private shown_hearts = -1;
  private shown_hearts_infinite = false;
  private shown_price = -1;
  private next_sync_at = 0;
  private last_auto_refresh_at = 0;
  private shown_ads_offline = false;
  private rv_offer_reported = false;
  private readonly has_purple_button: boolean;

  constructor()
  {
    const has_purple_button = AdsConfigService.isRewardedHeartsAllowed();
    const height_reduction = has_purple_button ? 0 : MoreLivesDialog.BTN_STEP;

    const body_w = LayoutScaler.widthFromDesign( MoreLivesDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( MoreLivesDialog.DESIGN_H - height_reduction );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.has_purple_button = has_purple_button;

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createHeartsImage();
    this.createTopBanner();
    this.createInfoFrame();
    this.createHeartsBadge();
    this.createBuyButton();
    if ( this.has_purple_button )
      this.createPurpleButton();
    this.createCloseButton();
    this.coins_badge = new DialogCoinsBadge( this.ctx, this );
    this.addChild( this.coins_badge );
  }

  public override onDialogShown(): void
  {
    if ( !this.has_purple_button || this.rv_offer_reported ) return;
    this.rv_offer_reported = true;
    reportRvOfferShown();
  }

  private createBackground(): void
  {
    const design_bg_y = MoreLivesDialog.BG_Y;
    const design_bg_h = MoreLivesDialog.BG_H - ( this.has_purple_button ? 0 : MoreLivesDialog.BTN_STEP );
    const design_bg_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.BG_W ) / 2;

    addMirroredDialogBg( this.ctx, this, design_bg_x, design_bg_y, MoreLivesDialog.BG_W, design_bg_h );
  }

  private createTopBanner(): void
  {
    const design_banner_w = 945;
    const design_banner_x = ( MoreLivesDialog.DESIGN_W - design_banner_w ) / 2;
    const design_banner_y = 187;
    const design_banner_h = 307;
    const design_title_y = 67;
    const design_title_h = 136;
    const design_font_size = 100;
    const design_outline_w = 20;

    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_banner_x ),
      LayoutScaler.yFromDesign( design_banner_y ),
      banner_w,
      banner_h,
      ImgID.UI_GoldenRibbon
    );
    this.addChild( img );

    const title_h = LayoutScaler.heightFromDesign( design_title_h );
    const MAGIC_NUM_CORRECTION = 0.7;
    const title_w = banner_w * MAGIC_NUM_CORRECTION;

    const title = new UITextElement(
      this.ctx,
      ( banner_w - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      title_h,
      {
        localization_key: "MORE_LIVES_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.fireworks_dialog_title_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        shadow_blur: LayoutScaler.widthFromDesign( 10 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
        auto_wrap: false,
      }
    );
    img.addChild( title );
  }

  private createHeartsImage(): void
  {
    const design_hearts_y = 20;
    const design_hearts_w = 615;
    const design_hearts_h = 237;
    const design_hearts_x = ( MoreLivesDialog.DESIGN_W - design_hearts_w ) / 2;

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_hearts_x ),
      LayoutScaler.yFromDesign( design_hearts_y ),
      LayoutScaler.widthFromDesign( design_hearts_w ),
      LayoutScaler.heightFromDesign( design_hearts_h ),
      ImgID.UI_Hearts
    );
    this.addChild( img );
  }

  private createInfoFrame(): void
  {
    const design_frame_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.INFO_FRAME_W ) / 2;

    const frame_w = LayoutScaler.widthFromDesign( MoreLivesDialog.INFO_FRAME_W );
    const frame_h = LayoutScaler.heightFromDesign( MoreLivesDialog.INFO_FRAME_H );
    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( MoreLivesDialog.INFO_FRAME_Y );

    const frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_GameStartContentBg );
    this.addChild( frame );

    const design_text_y = 169;
    const design_text_h = 70;
    const design_text_font_size = 50;

    const MAGIC_NUM_CORRECTION = 0.8;
    const text_w = frame_w * MAGIC_NUM_CORRECTION;
    const text_h = LayoutScaler.heightFromDesign( design_text_h );

    const text = new UITextElement(
      this.ctx,
      ( frame_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_y ),
      text_w,
      text_h,
      {
        localization_key: "MORE_LIVES_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
        font_weight: 900,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    frame.addChild( text );

    this.createCounterWithClock( frame );
  }

  private createCounterWithClock( frame: UIImage ): void
  {
    const design_clock_size = 118;
    const design_bg_w = 390;
    const design_bg_h = 90;

    const design_clock_x = 132;
    const design_bg_x = design_clock_x;
    const design_clock_y = 259;
    const design_bg_y = design_clock_y + ( design_clock_size - design_bg_h ) / 2;

    const clock_size = LayoutScaler.widthFromDesign( design_clock_size );
    const bg_w = LayoutScaler.widthFromDesign( design_bg_w );
    const bg_h = LayoutScaler.heightFromDesign( design_bg_h );

    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_bg_x ),
      LayoutScaler.yFromDesign( design_bg_y ),
      bg_w,
      bg_h,
      ImgID.UI_CounterBg,
      "counter_bg"
    );
    frame.addChild( bg );

    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_clock_x ),
      LayoutScaler.yFromDesign( design_clock_y ),
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    frame.addChild( clock );

    const design_hand_h = 100 * 0.8;
    const design_hand_w = design_hand_h * ( 24 / 73 );

    const hand = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( design_clock_size - design_hand_w ) / 2 ),
      LayoutScaler.yFromDesign( ( design_clock_size - design_hand_h ) / 2 ),
      LayoutScaler.widthFromDesign( design_hand_w ),
      LayoutScaler.heightFromDesign( design_hand_h ),
      ImgID.UI_ClockHand
    );
    clock.addChild( hand );

    const start_angle = 30 * Math.PI / 180;
    const tick_duration = 60000 / 60;

    hand.enableRotation();
    hand.setRotationImmediate( start_angle );
    hand.addTransform( new SteppedRotate(
      60,
      hand,
      true,
      null,
      start_angle + Math.PI * 2,
      performance.now() - tick_duration,
      60000,
      {
        infinite_loop: true,
        easing: Easing.Linear,
      }
    ) );

    this.shown_timer = heartTimerText( StatisticsStorage.load(), Date.now() );

    const design_time_text_x = 136;
    const design_time_text_w = 254;
    const design_time_font_size = 48;

    const time_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_time_text_x ),
      0,
      LayoutScaler.widthFromDesign( design_time_text_w ),
      bg_h,
      {
        text: this.shown_timer,
        font_size: LayoutScaler.textSizeFromDesign( design_time_font_size ),
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.geologica,
        font_weight: 900,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    bg.addChild( time_text );
    this.time_text = time_text;
  }

  private createHeartsBadge(): void
  {
    const design_outer_size = 204;
    const design_inner_size = 185;
    const design_heart_size = 164;
    const design_badge_y_offset = 59;
    const design_heart_y_offset = 7;

    const design_center_x = MoreLivesDialog.DESIGN_W / 2;
    const design_outer_y = MoreLivesDialog.INFO_FRAME_Y - design_badge_y_offset;

    const design_outer_x = design_center_x - design_outer_size / 2;
    const design_inner_x = design_center_x - design_inner_size / 2;
    const design_inner_y = design_outer_y + ( design_outer_size - design_inner_size ) / 2;
    const design_heart_x = design_center_x - design_heart_size / 2;
    const design_heart_y = design_outer_y + ( design_outer_size - design_heart_size ) / 2 + design_heart_y_offset;

    const outer_circle = new UICanvasRect(
      this.ctx,
      LayoutScaler.xFromDesign( design_outer_x ),
      LayoutScaler.yFromDesign( design_outer_y ),
      LayoutScaler.widthFromDesign( design_outer_size ),
      LayoutScaler.heightFromDesign( design_outer_size ),
      ColorScheme.white,
      LayoutScaler.widthFromDesign( design_outer_size / 2 )
    );
    this.addChild( outer_circle );

    const inner_circle = new UICanvasRect(
      this.ctx,
      LayoutScaler.xFromDesign( design_inner_x ),
      LayoutScaler.yFromDesign( design_inner_y ),
      LayoutScaler.widthFromDesign( design_inner_size ),
      LayoutScaler.heightFromDesign( design_inner_size ),
      ColorScheme.more_lives_badge_circle,
      LayoutScaler.widthFromDesign( design_inner_size / 2 )
    );
    this.addChild( inner_circle );

    const heart_w = LayoutScaler.widthFromDesign( design_heart_size );
    const heart_h = LayoutScaler.heightFromDesign( design_heart_size );

    const stats = StatisticsStorage.load();
    const hearts_infinite = stats.infinite_hearts_until > Date.now();
    const heart_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_heart_x ),
      LayoutScaler.yFromDesign( design_heart_y ),
      heart_w,
      heart_h,
      hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart
    );
    this.addChild( heart_icon );
    this.heart_icon = heart_icon;

    const design_count_text_y = -7;
    const design_count_font_size = 70;
    const design_count_shadow_blur = 6;
    const design_count_shadow_offset_y = 3;

    const heart_count_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_count_text_y ),
      heart_w,
      heart_h,
      {
        text: hearts_infinite ? "" : heartCountText( stats, Date.now() ),
        font_size: LayoutScaler.textSizeFromDesign( design_count_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        shadow_blur: LayoutScaler.widthFromDesign( design_count_shadow_blur ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_count_shadow_offset_y ),
      }
    );
    heart_icon.addChild( heart_count_text, true );
    this.heart_count_text = heart_count_text;
    this.shown_hearts = stats.hearts;
    this.shown_hearts_infinite = hearts_infinite;
  }

  private createBuyButton(): void
  {
    const design_btn_y = MoreLivesDialog.FIRST_BTN_Y + ( this.has_purple_button ? MoreLivesDialog.BTN_STEP : 0 );
    const design_btn_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.BTN_W ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( MoreLivesDialog.BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( MoreLivesDialog.BTN_H );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const design_font_size = 60;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( MoreLivesDialog.BTN_OUTLINE_W ),
      outline_offset_y: LayoutScaler.yFromDesign( MoreLivesDialog.BTN_OUTLINE_OFFSET_Y ),
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "BUY",
        ...text_options,
      }
    );

    const design_icon_size = 104;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx, 0, 0, icon_size, icon_size, ImgID.IconCoin
    );

    const stats = StatisticsStorage.load();
    this.shown_price = stats.heart_refill_price;

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text: String( this.shown_price ),
        ...text_options,
      }
    );
    this.price_text = price_text;

    const design_content_gap = 0;

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ buy_text, icon, price_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    btn.addChild( content, true );

    const hearts_full = stats.hearts >= stats.max_hearts;

    btn.setOnClick( () =>
    {
      if ( this.purchase_inflight || this.buy_conflict_locked )
        return;

      const current_stats = StatisticsStorage.load();
      if ( current_stats.hearts >= current_stats.max_hearts )
        return;

      if ( current_stats.coins < current_stats.heart_refill_price )
      {
        this.close();
        ScreenManager.showScreen( Screens.SHOP );
        return;
      }

      this.purchase_inflight = true;
      this.setBuyButtonActive( false );

      purchaseItem( "hearts_refill" )
        .then( r =>
        {
          const hearts_before = StatisticsStorage.load().hearts;
          adoptEconomyFromPlayer( r.player );
          const hearts_earned = Math.max( 0, StatisticsStorage.load().hearts - hearts_before );
          void logVirtualCurrencySpent( {
            currency_name: 'coins',
            amount: r.purchased.price,
            sink: 'hearts_refill',
          } );
          void logVirtualCurrencyEarned( {
            currency_name: 'hearts',
            amount: hearts_earned,
            source: 'hearts_refill',
          } );
          this.close();
        } )
        .catch( e =>
        {
          console.warn( '[Economy] hearts refill purchase failed', e );
          this.purchase_inflight = false;
          this.setBuyButtonActive( true );
          if ( e instanceof ApiError && e.status === 409 )
          {
            this.buy_conflict_locked = true;
            btn.setClickable( false );
            this.setBuyButtonActive( false );
          }
        } );
    } );

    this.addChild( btn );
    this.buy_btn = btn;
    this.buy_btn_texts = [ buy_text, price_text ];
    this.buy_btn_active = true;

    if ( hearts_full )
    {
      this.setBuyButtonActive( false );
      btn.setClickable( false );
    }
  }

  private setBuyButtonActive( active: boolean ): void
  {
    if ( !this.buy_btn || active === this.buy_btn_active )
      return;
    this.buy_btn_active = active;
    this.applyButtonLook( this.buy_btn, this.buy_btn_texts, active, ImgID.UI_GreenFrameBtn, ColorScheme.more_lives_buy_outline );
  }

  private setPurpleButtonActive( active: boolean ): void
  {
    if ( !this.purple_btn || active === this.purple_btn_active )
      return;
    this.purple_btn_active = active;
    this.applyButtonLook( this.purple_btn, this.purple_btn_texts, active, ImgID.UI_PurpleFrameBtn, ColorScheme.more_lives_purple_outline );
  }

  private applyButtonLook(
    btn: UINineSliceElement,
    texts: UITextElement[],
    active: boolean,
    active_img_id: ImgID,
    outline_color: string
  ): void
  {
    btn.setImgId( active ? active_img_id : ImgID.UI_NonActiveBtn, active ? "frame_btn" : "non_active_btn" );
    setButtonTextOutline( texts, active, outline_color );
    this.invalidateSubtree();
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.coins_badge?.sync( now );

    if ( now < this.next_sync_at )
      return;

    this.next_sync_at = now + 1000;
    this.syncHeartsState();
  }

  private syncHeartsState(): void
  {
    const stats = StatisticsStorage.load();
    const now_ms = Date.now();

    const timer = heartTimerText( stats, now_ms );
    if ( timer !== this.shown_timer )
    {
      this.shown_timer = timer;
      this.time_text?.setText( timer );
    }

    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    if ( stats.hearts !== this.shown_hearts || hearts_infinite !== this.shown_hearts_infinite )
    {
      this.shown_hearts = stats.hearts;
      this.shown_hearts_infinite = hearts_infinite;
      this.heart_icon?.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
      this.heart_count_text?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
    }

    const price = stats.heart_refill_price;
    if ( price !== this.shown_price )
    {
      this.shown_price = price;
      this.price_text?.setText( String( price ) );
    }

    const hearts_full = stats.hearts >= stats.max_hearts;
    const reward_cooldown_remaining = rewardedCooldownRemainingMs( 'hearts', now_ms );
    const reward_cooldown_active = reward_cooldown_remaining > 0;

    const reward_timer_text = rewardedCooldownTimerText( reward_cooldown_remaining );
    if ( reward_timer_text !== this.shown_reward_cooldown_timer )
    {
      this.shown_reward_cooldown_timer = reward_timer_text;
      this.reward_cooldown_text?.setText( reward_timer_text );
      this.reward_debug_render_reason = `timer:${reward_timer_text}`;
      this.invalidateSubtree();
    }

    if ( this.purple_btn
      && this.reward_active_content
      && this.reward_cooldown_content
      && reward_cooldown_active !== this.reward_cooldown_visible )
    {
      this.reward_cooldown_visible = reward_cooldown_active;
      if ( reward_cooldown_active )
      {
        this.purple_btn.removeChild( this.reward_active_content );
        this.purple_btn.addChild( this.reward_cooldown_content, true );
      }
      else
      {
        this.purple_btn.removeChild( this.reward_cooldown_content );
        this.purple_btn.addChild( this.reward_active_content, true );
      }
      this.reward_debug_render_reason = `state:${reward_cooldown_active ? 'cooldown' : 'available'}`;
      this.invalidateSubtree();
    }

    if ( this.buy_btn && !this.purchase_inflight && !this.buy_conflict_locked )
    {
      this.buy_btn.setClickable( !hearts_full );
      this.setBuyButtonActive( !hearts_full );
    }

    if ( this.purple_btn && !this.reward_inflight )
    {
      const disabled = hearts_full || reward_cooldown_active;
      const ads_offline = areAdsBlockedOffline();
      this.purple_btn.setClickable( !disabled );
      this.setPurpleButtonActive( !disabled && !ads_offline );
      if ( ads_offline !== this.shown_ads_offline )
      {
        this.shown_ads_offline = ads_offline;
        if ( !ads_offline ) hideOfflineAdTooltip();
        this.invalidateSubtree();
      }
    }

    if ( stats.hearts < stats.max_hearts
      && stats.next_heart_at > 0
      && now_ms >= stats.next_heart_at
      && now_ms - this.last_auto_refresh_at > 5000 )
    {
      this.last_auto_refresh_at = now_ms;
      void refreshPlayerFromServer();
    }
  }

  private createPurpleButton(): void
  {
    const design_btn_y = MoreLivesDialog.FIRST_BTN_Y;
    const design_btn_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.BTN_W ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( MoreLivesDialog.BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( MoreLivesDialog.BTN_H );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_PurpleFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const btn_content_text_options = {
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.more_lives_purple_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( MoreLivesDialog.BTN_OUTLINE_W ),
      outline_offset_y: LayoutScaler.yFromDesign( MoreLivesDialog.BTN_OUTLINE_OFFSET_Y ),
      ...TextStyleScheme.buttonShadow(),
    };

    const design_video_w = 94;
    const design_video_h = 84;

    const video_icon = new UIImage(
      this.ctx, 0, 0,
      LayoutScaler.widthFromDesign( design_video_w ), LayoutScaler.heightFromDesign( design_video_h ),
      ImgID.UI_VideoIcon
    );

    const design_heart_size = 174;

    const heart_icon = new UIImage(
      this.ctx, 0, 0,
      LayoutScaler.widthFromDesign( design_heart_size ), LayoutScaler.heightFromDesign( design_heart_size ),
      ImgID.IconHeart
    );

    const design_heart_text_y = -7;
    const design_heart_font_size = 70;
    const design_heart_shadow_blur = 6;
    const design_heart_shadow_offset_y = 3;

    const heart_plus_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_heart_text_y ),
      LayoutScaler.widthFromDesign( design_heart_size ),
      LayoutScaler.heightFromDesign( design_heart_size ),
      {
        text: "+1",
        font_size: LayoutScaler.textSizeFromDesign( design_heart_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        shadow_blur: LayoutScaler.widthFromDesign( design_heart_shadow_blur ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_heart_shadow_offset_y ),
      }
    );
    heart_icon.addChild( heart_plus_text, true );

    const design_btn_text_font_size = 53;

    const btn_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "MORE_LIVES_DIALOG.BTN_TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_btn_text_font_size ),
        ...btn_content_text_options,
      }
    );

    const design_content_gap = 0;

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ video_icon, heart_icon, btn_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    const clock_size = LayoutScaler.widthFromDesign( 104 );
    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 84 ),
      ( btn_h - clock_size ) / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    const hand_h = LayoutScaler.heightFromDesign( 80 );
    const hand_w = hand_h * ( 24 / 73 );
    const hand = new UIImage(
      this.ctx,
      ( clock_size - hand_w ) / 2,
      ( clock_size - hand_h ) / 2,
      hand_w,
      hand_h,
      ImgID.UI_ClockHand
    );
    hand.enableRotation();
    hand.setRotationImmediate( Math.PI / 6 );
    clock.addChild( hand );

    const cooldown_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 225 ),
      LayoutScaler.yFromDesign( 75 ),
      LayoutScaler.widthFromDesign( 350 ),
      LayoutScaler.heightFromDesign( 100 ),
      {
        text: "00:00:00",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    const cooldown_content = new UIElementHolder( this.ctx, 0, 0, btn_w, btn_h );
    cooldown_content.addChild( clock, true );
    cooldown_content.addChild( cooldown_text, true );
    content.setClickable( false );
    cooldown_content.setClickable( false );
    btn.addChild( content, true );

    const stats = StatisticsStorage.load();
    const can_watch = stats.hearts < stats.max_hearts;

    btn.setOnClick( () => { void this.onWatchAdForHeart( btn ); } );

    this.addChild( btn );
    this.purple_btn = btn;
    this.purple_btn_texts = [ btn_text ];
    this.purple_btn_active = true;

    if ( !can_watch )
    {
      this.setPurpleButtonActive( false );
      btn.setClickable( false );
    }
    this.reward_active_content = content;
    this.reward_cooldown_content = cooldown_content;
    this.reward_cooldown_text = cooldown_text;
    setButtonTextOutline( [ cooldown_text ], false, ColorScheme.more_lives_purple_outline );
    this.syncHeartsState();
  }

  private async onWatchAdForHeart( btn: UINineSliceElement ): Promise<void>
  {
    if ( this.reward_inflight || isRewardedCooldownActive( 'hearts' ) )
      return;

    const stats = StatisticsStorage.load();
    if ( stats.hearts >= stats.max_hearts )
      return;

    if ( areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( btn );
      return;
    }

    this.reward_inflight = true;
    this.setPurpleButtonActive( false );

    try
    {
      const res = await showRewarded( "rewarded" );
      if ( !res.rewarded )
      {
        if ( !res.shown ) showAdUnavailableTooltip( btn );
        return;
      }

      const r = await rewardHeart();
      adoptEconomyFromPlayer( r.player );
      startRewardedCooldown( 'hearts' );
      void logVirtualCurrencyEarned( {
        currency_name: 'hearts',
        amount: 1,
        source: 'rewarded_ad',
      } );
      void logRewardedCompleted( {
        placement: 'rewarded',
        reward_type: 'heart',
        reward_value: 1,
      } );
    }
    catch ( e )
    {
      console.warn( "[Economy] reward heart failed", e );
    }
    finally
    {
      this.reward_inflight = false;
      this.syncHeartsState();
    }
  }

  public override cleanup(): void
  {
    hideOfflineAdTooltip();
    if ( this.purple_btn && this.reward_active_content && this.reward_cooldown_content )
    {
      this.purple_btn.removeChild( this.reward_active_content );
      this.purple_btn.removeChild( this.reward_cooldown_content );
      this.reward_active_content.cleanup();
      this.reward_cooldown_content.cleanup();
    }
    super.cleanup();
  }

  private createCloseButton(): void
  {
    const design_btn_size = 110;
    const design_btn_y = 164;
    const design_btn_x = 812;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => this.close() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public static open(): MoreLivesDialog
  {
    return DialogManager.openPooled( "MoreLivesDialog", undefined, () => new MoreLivesDialog() ) as MoreLivesDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse(): void
  {
    if ( this.has_purple_button !== AdsConfigService.isRewardedHeartsAllowed() )
      throw new Error( "MoreLivesDialog layout changed" );
    this.purchase_inflight = false;
    this.buy_conflict_locked = false;
    this.reward_inflight = false;
    this.rv_offer_reported = false;
    this.reward_cooldown_visible = false;
    this.shown_reward_cooldown_timer = "";
    this.shown_timer = "";
    this.shown_hearts = -1;
    this.shown_hearts_infinite = false;
    this.shown_price = -1;
    this.shown_ads_offline = false;
    this.next_sync_at = 0;
    this.last_auto_refresh_at = 0;
    this.syncHeartsState();
  }

  public override onPooled(): void
  {
    hideOfflineAdTooltip();
  }

  public name(): string
  {
    return "MoreLivesDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.reward_debug_render_reason )
    {
      this.reward_debug_render_reason = null;
    }

    this.startRender();
    this.finishRender();
  }
}
